<script setup lang="ts">
  import { computed, ref, watch, getCurrentInstance } from "vue";
  import { useRoute } from "vue-router";
  import { storeToRefs } from "pinia";
  import { Loader2 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import MasterReportActions from "@/components/App/AppHeader/MasterReportActions.vue";
  import ReportThemeActions from "@/components/App/AppHeader/ReportThemeActions.vue";
  import ChatPageActions from "@/components/App/AppHeader/ChatPageActions.vue";

  // Survey components
  import SurveyLocales from "@/components/Survey/SurveyLocales.vue";
  import EditButton from "@/components/Survey/Components/EditButton.vue";
  import HistoryButton from "@/components/Survey/History/HistoryButton.vue";
  import CardsExportButton from "@/components/Survey/Components/CardsExportButton.vue";
  import ResultsSetupButton from "@/components/Survey/Components/ResultsSetupButton.vue";
  import LearningsButton from "@/components/Survey/Components/LearningsButton.vue";
  import ContextSaveButton from "@/components/Survey/Context/ContextSaveButton.vue";
  import MailingButton from "@/components/Mailing/MailingButton.vue";
  import AppearanceBubbles from "@/components/App/AppearanceBubbles.vue";
  import NavButton from "@/components/App/NavButton.vue";
  import PricerButton from "@/components/Admin/Pricer/PricerButton.vue";

  // Stores
  import { useCurrentUserStore } from "@/stores/currentUser";
  import { useSurveyStore } from "@/stores/survey/survey";
  import { useTemplateStore } from "@/stores/mailing/template";
  import { useMCampaignStore } from "@/stores/mCampaign";
  import { useMCampaignActionsStore } from "@/stores/mCampaignActions";
  import { useMCampaignTargetsStore } from "@/stores/mCampaignTargets";
  import { useMCampaignQuotasStore } from "@/stores/mCampaignQuotas";
  import { useCCampaignsStore } from "@/stores/cCampaigns";
  import { useCAdsetsStore } from "@/stores/cAdsets";
  import { useTaskProgressesStore } from "@/stores/taskProgresses";
  import { usePricerProjectStore } from "@/stores/pricer/pricerProject";
  import { useReportsV2Store } from "@/stores/survey/reportsV2/reportsV2";
  import { useGridsStore } from "@/stores/survey/grids/grids";
  import { useMCampaignUnsavedChanges } from "@/utils/composables/useMCampaignUnsavedChanges";
  import ResultsV2SaveButton from "@/components/Survey/ResultsV2/ResultsV2SaveButton.vue";
  import ResultsV2DownloadButton from "@/components/Survey/ResultsV2/ResultsV2DownloadButton.vue";
  import ResultsV2ShareButton from "@/components/Survey/ResultsV2/ResultsV2ShareButton.vue";
  import { useResultsV2Store } from "@/stores/survey/resultsV2/resultsV2";
  import { Download } from "lucide-vue-next";

  const route = useRoute();

  // Route groups
  const programmationViews = ["SurveySetup", "SurveyQuestions", "SurveyTargets"];
  const showLocalesViews = [
    "SurveyQuestions",
    "SurveyProgress",
    "SurveyCompletion",
    "SurveyGrids",
    "SurveyTargets",
  ];

  const routeName = computed(() => route.name as string);

  const { currentGrid } = storeToRefs(useGridsStore());
  const showLocales = computed(
    () =>
      showLocalesViews.includes(routeName.value) &&
      !(routeName.value === "SurveyGrids" && currentGrid.value?.grid_type === "population"),
  );
  const isCardsReview = computed(() => routeName.value === "SurveyQuestions" && route.hash === "#review");

  // Route type detection
  const isSurveyRoute = computed(() => {
    const path = route.path;
    return path.match(/^\/surveys\/[^/]+/) || path.match(/^\/lm_surveys\/[^/]+/);
  });

  const isTemplateRoute = computed(() => {
    return route.path.match(/^\/mailing\/templates\/[^/]+/);
  });

  const isMCampaignRoute = computed(() => {
    return route.path.match(/^\/admin\/m_campaigns\/[^/]+/);
  });

  const pricerProjectEditRoutes = ["PricerProjectEdit", "PricerProjectTargets", "PricerProjectSummary"];
  const isPricerProjectRoute = computed(() => pricerProjectEditRoutes.includes(routeName.value));

  const isMasterReportRoute = computed(() => {
    return route.path.match(/^\/master-reports\/[^/]+/);
  });

  const isReportThemeDetailRoute = computed(() =>
    route.name === 'ReportThemeDetail'
  );

  const isChatPageCustomizerRoute = computed(() => routeName.value === "ChatPageCustomizer");

  // Survey store
  const surveyStore = useSurveyStore();
  const { survey, canWriteSurvey } = storeToRefs(surveyStore);

  const { isAdmin } = storeToRefs(useCurrentUserStore());

  const showBubbles = computed(() => {
    return programmationViews.includes(routeName.value);
  });

  // Template store
  const templateStore = useTemplateStore();
  const { hasTemplateChanged } = storeToRefs(templateStore);
  const { handleTemplateSave } = templateStore;

  // MCampaign stores
  const mCampaignStore = useMCampaignStore();
  const { fetchMCampaign, saveMCampaign, pushMCampaign, saveMCampaignBids, saveMCampaignActions, setMCampaignSync } =
    mCampaignStore;
  const { hasMCampaignChanged, hasMCampaignBidsChanged, hasMCampaignActionsChanged, currentMCampaign, isSync } =
    storeToRefs(mCampaignStore);

  const mCampaignActionsStore = useMCampaignActionsStore();
  const { hasBeenSubmitted: hasActionsBeenSubmitted, areAllActionsValid } = storeToRefs(mCampaignActionsStore);
  const { validateActionsForSave, clearActionsSubmission } = mCampaignActionsStore;

  const mCampaignTargetsStore = useMCampaignTargetsStore();
  const { hasTargetsChanged } = storeToRefs(mCampaignTargetsStore);
  const { saveMCampaignTargets } = mCampaignTargetsStore;

  const mCampaignQuotasStore = useMCampaignQuotasStore();
  const { hasQuotasChanged } = storeToRefs(mCampaignQuotasStore);
  const { saveMCampaignQuota } = mCampaignQuotasStore;

  const cCampaignsStore = useCCampaignsStore();
  const { hasCCampaignsChanged } = storeToRefs(cCampaignsStore);
  const { saveCCampaignsBudgets } = cCampaignsStore;

  const cAdsetsStore = useCAdsetsStore();
  const { hasCAdsetsChanged } = storeToRefs(cAdsetsStore);
  const { saveCAdsetsBudgets } = cAdsetsStore;

  const taskProgressesStore = useTaskProgressesStore();
  const { lastProgressUpdate, taskProgresses } = storeToRefs(taskProgressesStore);

  const isSavingMCampaign = ref(false);
  const isStartingPush = ref(false);
  const savedDuringPush = ref(false);

  // Same source of truth as the leave-confirmation guard, so Save and the dialog never drift.
  const { hasUnsavedChanges } = useMCampaignUnsavedChanges();

  const hasMCampaignSaveErrors = computed(
    () => routeName.value === "MCampaignActions" && hasActionsBeenSubmitted.value && !areAllActionsValid.value,
  );

  const isPushing = computed(() =>
    taskProgresses.value.some((task: { status: string; taskInfo?: { type: string; mCampaignToken: string } }) => {
      return (
        !["Complete", "Failed"].includes(task.status) &&
        task.taskInfo?.type === "push_m_campaign" &&
        task.taskInfo?.mCampaignToken === currentMCampaign.value?.token
      );
    }),
  );

  const canPushMCampaign = computed(() => {
    const isClean =
      !hasCAdsetsChanged.value && !hasCCampaignsChanged.value && !hasMCampaignChanged.value && !isSavingMCampaign.value;
    if (isClean) return isPushing.value ? savedDuringPush.value : !isSync.value;
    return false;
  });

  const handleSaveMCampaign = async () => {
    isSavingMCampaign.value = true;
    if (["CampaignTargets", "CampaignQuotas"].includes(routeName.value)) {
      await Promise.all([
        hasTargetsChanged.value ? saveMCampaignTargets() : Promise.resolve(),
        hasQuotasChanged.value ? saveMCampaignQuota() : Promise.resolve(),
      ]);
    } else if (routeName.value === "MCampaignBudgets") {
      await Promise.all([
        hasCCampaignsChanged.value ? saveCCampaignsBudgets(route.params.token as string) : Promise.resolve(),
        hasCAdsetsChanged.value ? saveCAdsetsBudgets(route.params.token as string) : Promise.resolve(),
        hasMCampaignBidsChanged.value ? saveMCampaignBids() : Promise.resolve(),
      ]);
      await fetchMCampaign(route.params.token as string);
    } else if (routeName.value === "MCampaignActions") {
      if (hasMCampaignActionsChanged.value && validateActionsForSave()) {
        if (await saveMCampaignActions()) clearActionsSubmission();
      }
    } else {
      if (hasMCampaignChanged.value) await saveMCampaign();
    }
    isSavingMCampaign.value = false;
    if (isPushing.value) savedDuringPush.value = true;
  };

  const handlePushMCampaign = async () => {
    isStartingPush.value = true;
    if (canPushMCampaign.value) {
      await pushMCampaign(route.params.token as string);
      savedDuringPush.value = false;
    }
    isStartingPush.value = false;
  };

  watch(lastProgressUpdate, (task) => {
    const isPushComplete =
      task?.taskInfo?.type === "push_m_campaign" &&
      task?.taskInfo?.mCampaignToken === currentMCampaign.value?.token &&
      task?.status === "Complete";

    if (isPushComplete && !savedDuringPush.value) {
      setMCampaignSync(true);
    }
  });

  // Pricer store
  const pricerProjectStore = usePricerProjectStore();
  const { project, hasProjectChanged, targetHasMissingParams, projectHasMissingParams } =
    storeToRefs(pricerProjectStore);
  const { saveProject } = pricerProjectStore;

  const pricerHasMandatoryIssues = computed(() => targetHasMissingParams.value || projectHasMissingParams.value);
  const hasObsoletePricingVersion = computed(() => project.value?.has_obsolete_pricing_version);

  const handlePricerSave = async () => await saveProject();

  // Reports V2 store
  const reportsV2Store = useReportsV2Store();
  const {
    hasUnsavedChanges: hasReportV2UnsavedChanges,
    saving: isSavingReportV2,
    generating: isGeneratingReportV2,
    hasSlides: hasReportV2Slides,
  } = storeToRefs(reportsV2Store);
  const isReportV2SlidesRoute = computed(() => routeName.value === "ReportV2Slides");
  const handleSaveReportV2 = () => reportsV2Store.saveReport();

  // Cable instance for WebSocket communication (needed for report generation)
  const vueInstance = getCurrentInstance();
  const cable = computed(() => vueInstance?.proxy?.$cable);
  const handleDownloadReportV2 = () => reportsV2Store.generateReport(cable.value, vueInstance);

  // Results V2
  const resultsV2Store = useResultsV2Store();
  const { editMode } = storeToRefs(resultsV2Store);
  const isResultsV2Route = computed(() =>
    ["SurveyResultsV2", "SurveyResultsV2View", "SurveyChartsLeadMagnets"].includes(routeName.value),
  );
</script>

<template>
  <div class="flex items-center gap-3">
    <!-- Reports V2 Slides Actions (must be before Survey to avoid path conflict) -->
    <template v-if="isReportV2SlidesRoute">
      <Button
        :disabled="!hasReportV2UnsavedChanges || isSavingReportV2"
        @click="handleSaveReportV2">
        <Loader2
          v-if="isSavingReportV2"
          class="w-4 h-4 mr-2 animate-spin" />
        Save
      </Button>
      <Button
        :disabled="isGeneratingReportV2 || !hasReportV2Slides"
        @click="handleDownloadReportV2">
        <Loader2
          v-if="isGeneratingReportV2"
          class="w-4 h-4 mr-2 animate-spin" />
        <Download
          v-else
          class="w-4 h-4 mr-2" />
        Download
      </Button>
    </template>

    <!-- Results V2 Actions -->
    <template v-else-if="isResultsV2Route">
      <ResultsV2SaveButton v-if="editMode" />
      <template v-else>
        <ResultsV2ShareButton />
        <ResultsV2DownloadButton />
      </template>
    </template>

    <!-- Survey Actions -->
    <template v-else-if="isSurveyRoute">
      <!-- Locales selector for specific views -->
      <SurveyLocales v-if="showLocales" />

      <!-- AppearanceBubbles for programmation views -->
      <AppearanceBubbles v-if="showBubbles" />

      <!-- Route-specific buttons -->
      <CardsExportButton v-if="isCardsReview" />
      <HistoryButton v-if="routeName === 'SurveyQuestions' && isAdmin" />
      <EditButton
        v-if="programmationViews.includes(routeName)"
        :is-read-only="!canWriteSurvey"
        :survey="survey" />
      <ResultsSetupButton v-else-if="routeName === 'SurveyResultsSetup'" />
      <LearningsButton v-else-if="routeName === 'SurveyLearnings'" />
      <MailingButton v-else-if="routeName === 'SurveyTestContacts'" />
      <ContextSaveButton v-else-if="routeName === 'SurveyContext'" />
    </template>

    <!-- Template Actions -->
    <template v-else-if="isTemplateRoute">
      <NavButton
        v-if="routeName === 'TemplateSettings'"
        :can-trigger="hasTemplateChanged"
        :action-function="handleTemplateSave" />
    </template>

    <!-- Pricer Actions -->
    <template v-else-if="isPricerProjectRoute">
      <PricerButton
        :can-trigger="hasProjectChanged && !pricerHasMandatoryIssues && !hasObsoletePricingVersion"
        :has-missing-fields="pricerHasMandatoryIssues"
        :action-function="handlePricerSave" />
    </template>

    <!-- MCampaign/Ads Actions -->
    <template v-else-if="isMCampaignRoute">
      <NavButton
        :can-trigger="hasUnsavedChanges"
        :has-errors="hasMCampaignSaveErrors"
        :action-function="handleSaveMCampaign"
        :loading="isSavingMCampaign" />
      <NavButton
        v-if="routeName === 'MCampaignBudgets'"
        :can-trigger="canPushMCampaign"
        :action-function="handlePushMCampaign"
        :content="'PUSH'"
        :loading="isStartingPush" />
    </template>

    <!-- Master Report Actions -->
    <MasterReportActions v-else-if="isMasterReportRoute" />

    <!-- Report Theme Actions -->
    <ReportThemeActions v-else-if="isReportThemeDetailRoute" />

    <!-- Chat Page Customizer Actions -->
    <ChatPageActions v-else-if="isChatPageCustomizerRoute" />
  </div>
</template>
